import React, { useState } from "react";
import { Heart, Leaf, Sparkles, Users } from "lucide-react";
import { submitContact } from "../lib/api";
import { toast } from "sonner";

const AboutPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      setBusy(true);
      await submitContact(form.name, form.email, form.message);
      toast.success("Thanks for reaching out — we'll reply soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send message. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const pillars = [
    {
      icon: Heart,
      title: "Made for movement",
      body: "Every product is engineered around the simple belief that joyful movement changes lives.",
    },
    {
      icon: Leaf,
      title: "Built to last",
      body: "Commercial-grade materials, rigorous testing, and a lifetime frame warranty — because durability is sustainability.",
    },
    {
      icon: Sparkles,
      title: "Designed beautifully",
      body: "Furniture-grade aesthetics that feel at home in any space — not tucked away in the garage.",
    },
    {
      icon: Users,
      title: "For everybody",
      body: "Our community spans ages 18 to 80. Low-impact, high-result workouts for every body.",
    },
  ];

  return (
    <main className="bg-[#FFF2EC]">
      {/* Hero */}
      <section className="relative overflow-hidden coral-soft">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24 text-center">
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#DD4D2A] font-semibold mb-4">
            Our Story
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl font-semibold max-w-3xl mx-auto leading-[1.05]">
            Move better. <span className="italic text-[#DD4D2A]">Feel</span>{" "}
            better. <span className="italic">Live better.</span>
          </h1>
          <p className="mt-6 text-lg text-[#1A1410]/70 max-w-2xl mx-auto">
            George Venture Brands was founded on a simple idea: fitness should
            meet you where you are. No intimidating gyms. No pounding on your
            joints. Just bouncy, brilliant workouts that fit into real life.
          </p>
        </div>
      </section>

      {/* Image band */}
      <section className="grid md:grid-cols-3 gap-0">
        {[
          "https://images.pexels.com/photos/6571945/pexels-photo-6571945.jpeg",
          "https://images.unsplash.com/photo-1606902965551-dce093cda6e7",
          "https://images.unsplash.com/photo-1774414489812-f863c5bd32f4",
        ].map((src, i) => (
          <div
            key={i}
            className="relative aspect-[4/3] md:aspect-square overflow-hidden"
          >
            <img
              src={src}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        ))}
      </section>

      {/* Pillars */}
      <section className="bg-[#FFE4D6]">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.35em] text-[#DD4D2A] font-semibold mb-3">
              What we stand for
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-semibold">
              Four pillars, <span className="italic">one mission.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {pillars.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-[#FFF2EC] rounded-3xl p-7 border border-[#1A1410]/8"
              >
                <div className="w-11 h-11 rounded-full bg-[#DD4D2A] text-white flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif-display text-2xl font-semibold">
                  {title}
                </h3>
                <p className="mt-2 text-[#1A1410]/70">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-[#FFF2EC]">
        <div className="max-w-3xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-[0.35em] text-[#DD4D2A] font-semibold mb-3">
              Get in touch
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-semibold">
              We'd love to <span className="italic">hear from you</span>
            </h2>
          </div>
          <form
            onSubmit={submit}
            className="bg-white rounded-3xl border border-[#1A1410]/10 p-6 md:p-8 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Name"
                className="w-full px-4 py-3 rounded-xl border border-[#1A1410]/15 outline-none focus:border-[#DD4D2A] bg-[#FFF2EC]/50"
              />
              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                type="email"
                className="w-full px-4 py-3 rounded-xl border border-[#1A1410]/15 outline-none focus:border-[#DD4D2A] bg-[#FFF2EC]/50"
              />
            </div>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="How can we help?"
              rows={5}
              className="w-full px-4 py-3 rounded-xl border border-[#1A1410]/15 outline-none focus:border-[#DD4D2A] bg-[#FFF2EC]/50"
            />
            <button
              disabled={busy}
              className="btn-coral w-full py-4 rounded-full text-sm uppercase disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
